import {
  type CatalogLanguage,
  getCatalog,
  type TranslationKeys,
} from "@/hooks/useTranslation/catalogs"
import type { ConformityDetail } from "../../../api/conformityApi"

/**
 * Default cover email copy for a conformity declaration.
 *
 * Language selection: when the caller supplies an `override` — the editor
 * passes its EN/DE preview toggle — the cover flips in lockstep with the
 * PDF preview so the reviewer sees a single coherent language while
 * drafting. Otherwise we fall back to the persisted `detail.language`
 * (customer preference), which mirrors the backend's `_resolve_lang`
 * (declaration.language → 'en'). Product/article scopes persist
 * language=None so their fallback is EN, matching the PDF.
 *
 * The row's persisted `language` is still what actually ships to the
 * customer — the override only steers the DRAFT copy the reviewer edits.
 */
type CoverLanguage = "en" | "de"

export function coverLanguage(
  detail: ConformityDetail,
  override?: CoverLanguage,
): CoverLanguage {
  if (override) return override
  return detail.language?.toLowerCase() === "de" ? "de" : "en"
}

/** Catalogs the cover copy reads, whatever the UI language: the conformity
 *  editor route renders under `<CatalogGate languages={COVER_CATALOGS}>`. */
export const COVER_CATALOGS: readonly CatalogLanguage[] = ["EN", "DE"]

// Plain {var} interpolation only — no ICU plural support. The cover keys
// must stay ICU-free: an ICU block here would render as raw syntax in a
// customer-facing email.
function coverTranslate(
  lang: CoverLanguage,
  key: TranslationKeys,
  vars?: Record<string, string | number>,
): string {
  const catalog = getCatalog(lang === "en" ? "EN" : "DE")
  let text = catalog?.[key] || key
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      text = text.replaceAll(`{${k}}`, String(v))
    }
  }
  return text
}

/** v1 rows carry a single framework; v2 rows (framework=null) list their
 *  selected keys instead. The v2 default is a placeholder only — approve
 *  overwrites it with the backend's seed-based cover note. */
function frameworkText(detail: ConformityDetail): string {
  return detail.framework ?? detail.frameworkKeys.join(", ")
}

export function subjectLine(detail: ConformityDetail): string {
  if (detail.scope === "customer") {
    return [detail.customerName, detail.productName].filter(Boolean).join(" · ")
  }
  if (detail.scope === "product") {
    return [detail.productNumber, detail.productName]
      .filter(Boolean)
      .join(" · ")
  }
  return [detail.articleNumber, detail.articleName].filter(Boolean).join(" · ")
}

/** Framework-aware default subject for the outgoing email. Shown on drafts
 *  so the reviewer sees a sensible starting point; user edits are saved as
 *  the actual value and preserved through approve.
 *
 *  Keep short — the backend's auto-draft on approve produces a richer
 *  clean/contains-aware version. Only used when the row is still a draft. */
export function buildDefaultSubject(
  detail: ConformityDetail,
  override?: CoverLanguage,
): string {
  const lang = coverLanguage(detail, override)
  const subj = subjectLine(detail) || detail.reference || frameworkText(detail)
  return coverTranslate(lang, "MC_CS_CONF_EDIT_DEFAULT_SUBJECT", {
    framework: frameworkText(detail),
    subject: subj,
  })
}

export function buildDefaultBody(
  detail: ConformityDetail,
  override?: CoverLanguage,
): string {
  const lang = coverLanguage(detail, override)
  const who =
    detail.customerName ||
    coverTranslate(lang, "MC_CS_CONF_EDIT_DEFAULT_RECIPIENT_FALLBACK")
  const subj =
    subjectLine(detail) ||
    detail.articleNumber ||
    coverTranslate(lang, "MC_CS_CONF_EDIT_DEFAULT_SUBJECT_FALLBACK")
  const lines = [
    coverTranslate(lang, "MC_CS_CONF_EDIT_DEFAULT_GREETING", { who }),
    "",
    coverTranslate(lang, "MC_CS_CONF_EDIT_DEFAULT_BODY_LINE", {
      subject: subj,
      framework: frameworkText(detail),
    }),
  ]
  // Gap-aware: the PDF renders the standard compliance statement even on
  // partial-data drafts (policy 2026-07-29), but the operator has held to
  // review — add a one-line qualifier acknowledging pending supplier data
  // so the cover email doesn't read as an unqualified "here is your
  // conformity" while the PDF's own copy stays framework-standard.
  if (detail.hasGap) {
    lines.push(
      "",
      coverTranslate(lang, "MC_CS_CONF_EDIT_DEFAULT_BODY_LINE_GAP"),
    )
  }
  lines.push("", coverTranslate(lang, "MC_CS_CONF_EDIT_DEFAULT_CLOSING"))
  return lines.join("\n")
}
